import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import type { CreateFinancialYearRequest } from '../../services/financialYearsApi';

interface CreateFinancialYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateFinancialYearRequest) => void;
  submitting: boolean;
}

const EMPTY_FORM = { code: '', name: '', startDate: '', endDate: '' };

export const CreateFinancialYearDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: CreateFinancialYearDialogProps) => {
  const [form, setForm] = useState(EMPTY_FORM);

  const handleOpenChange = (next: boolean) => {
    if (!next) setForm(EMPTY_FORM);
    onOpenChange(next);
  };

  const set = (field: keyof typeof EMPTY_FORM) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const valid =
    form.code.trim().length > 0 &&
    form.name.trim().length > 0 &&
    form.startDate.length > 0 &&
    form.endDate.length > 0 &&
    form.endDate > form.startDate;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({
      code: form.code.trim(),
      name: form.name.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Financial Year</DialogTitle>
          <DialogDescription>
            Enter the code, name and date boundaries for the new financial year.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div>
              <Label htmlFor="fy-code">Code</Label>
              <Input
                id="fy-code"
                placeholder="e.g. FY2026"
                value={form.code}
                onChange={(e) => set('code')(e.target.value)}
                disabled={submitting}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="fy-name">Name</Label>
              <Input
                id="fy-name"
                placeholder="e.g. Financial Year 2026"
                value={form.name}
                onChange={(e) => set('name')(e.target.value)}
                disabled={submitting}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div>
              <Label htmlFor="fy-start">Start Date</Label>
              <DatePicker
                id="fy-start"
                value={form.startDate}
                onChange={set('startDate')}
                disabled={submitting}
                max={form.endDate || undefined}
                className="mt-1 w-full"
              />
            </div>
            <div>
              <Label htmlFor="fy-end">End Date</Label>
              <DatePicker
                id="fy-end"
                value={form.endDate}
                onChange={set('endDate')}
                disabled={submitting}
                min={form.startDate || undefined}
                className="mt-1 w-full"
              />
            </div>
          </div>

          {form.endDate && form.startDate && form.endDate <= form.startDate && (
            <p className="text-xs text-red-600">End date must be after start date.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || submitting}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
